import { isEmpty } from 'lodash-es';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ComposableDashboardFiltersEnum,
  composableDashboardFiltersLabelValue,
  IComposableAnalyticsGlobalFilters,
  IComposableDashboardFiltersContext,
  IDependentFilters,
} from 'wisestep-api';

import { useUserContext } from '../../../context';
import {
  getEnumFromLabel,
  SmartDateOptions,
} from '../../../features/analytics/utils/smartDateSelectHelper';
import { useComposableDashboardsContext } from '../hooks/useComposableDashboardsContext';

//Context for storing state of globally applied filters in the dashboard
export const ComposableDashboardFiltersContext = createContext<
  (IComposableDashboardFiltersContext & { restructuredFilters: any[] }) | null
>(null);

export default function ComposableDashboardFiltersContextProvider(
  props: PropsWithChildren<IComposableDashboardFiltersContext | null>
) {
  const loggedInUser = useUserContext().userDTO;
  const [params] = useSearchParams();
  const userEmail = params?.get('email');
  const { selectedDashboard } = useComposableDashboardsContext();
  const [dependentFilters, setDependentFilters] = useState<IDependentFilters>({
    smartFilters: [],
  });
  const checkAndAddDependentFilters = (filter: IComposableAnalyticsGlobalFilters) => {
    let initializeDependentFilters;
    if (!isEmpty(filter?.['smartFilters']) || !isEmpty(filter?.['singleDateField'])) {
      initializeDependentFilters = {
        smartFilters: filter?.['smartFilters']?.map((f) => SmartDateOptions[f]) ?? [],
        singleDateField: filter?.['singleDateField'],
      };
    }
    setDependentFilters((prev) => ({ ...prev, ...initializeDependentFilters }));
  };
  const overriddenFilters = useRef<IComposableAnalyticsGlobalFilters>({});
  const initialiseSelectedFilters = useCallback(() => {
    const listOfFilters =
      (Object.keys(
        selectedDashboard?.appliedDashboardFilters ?? {}
      ) as ComposableDashboardFiltersEnum[]) ?? [];
    if (userEmail) {
      return listOfFilters.filter((filter) => filter != ComposableDashboardFiltersEnum.RECRUITER);
    }
    return listOfFilters;
  }, [selectedDashboard?.appliedDashboardFilters, userEmail]);

  useEffect(() => {
    const listOfFilters =
      (Object.keys(
        selectedDashboard?.appliedDashboardFilters ?? {}
      ) as ComposableDashboardFiltersEnum[]) ?? [];
    setFiltersAddedToDashboard(listOfFilters);
  }, [selectedDashboard?.appliedDashboardFilters]);

  const initialiseSelectedFiltersPayload = useCallback((): IComposableAnalyticsGlobalFilters => {
    let initializeFilters: IComposableAnalyticsGlobalFilters = {};
    const filterState = Object.values(selectedDashboard?.appliedDashboardFilters ?? {});
    filterState.forEach((filter) => {
      initializeFilters = { ...initializeFilters, ...filter };
      checkAndAddDependentFilters(filter);
    });
    if (userEmail) {
      initializeFilters = {
        ...initializeFilters,
        recruiterId: userEmail,
        recruiterNames: [userEmail ?? ''],
        ...(userEmail === loggedInUser?.email && { filterScope: 'loggedInUser' }),
      };
    }
    initializeFilters = { ...initializeFilters, ...overriddenFilters.current };
    return initializeFilters;
  }, [loggedInUser?.email, selectedDashboard?.appliedDashboardFilters, userEmail]);

  const [filters, setFilters] = useState<IComposableAnalyticsGlobalFilters>(() =>
    initialiseSelectedFiltersPayload()
  );

  const restructuredFilters = useMemo(
    () =>
      Object.keys(filters ?? {}).reduce(
        (finalFilter: { filterName: string; values: any[] }[], filterKey) => {
          let filterName = filterKey.replace(/([A-Z])/g, '_$1').toUpperCase();
          let values: any[] = [];
          if (
            [
              'TO_DATE',
              'DATES_UNION_BY',
              'SMART_FILTERS',
              'CLIENT_SPOC_EMAILS',
              'TEAM_NAMES',
              'FILTER_SCOPE',
              'RECRUITER_ID',
            ].includes(filterName)
          ) {
            return finalFilter;
          } else if (filterName == 'FROM_DATE') {
            filterName = 'DATE_RANGE';
            values = [
              JSON.stringify({
                filterBy: filters?.datesUnionBy ?? '',
                fromDate: filters.fromDate,
                toDate: filters.toDate,
              }),
            ];
          } else if (filterName == 'CURRENT_STAGE_CURRENT_STATUS_PAIRS') {
            filterName = 'STAGE_STATUS';
            values = filters?.[filterKey]
              ?.reduce((result, item) => {
                const stage = item.applicationStage;
                const state = item.applicationState;
                const existingStage = result.find((obj) => obj.stage === stage);
                if (existingStage) {
                  existingStage.selectedStatuses.push(state);
                } else {
                  result.push({
                    stage: stage,
                    selectedStatuses: [state],
                  });
                }
                return result;
              }, [])
              .map((filterValue) => JSON.stringify(filterValue));
          } else if (filterName == 'CANDIDATE_STAGE_STATUS_PIPELINE_DATA') {
            filterName = 'candidateStageStatusPipelineData';
            values = [filters.candidateStageStatusPipelineData as string];
          } else if (filterName == 'CLIENT_IDS') {
            filterName = 'CLIENT_ID';
            values = filters.clientIds as string[];
          } else if (filterName == 'CLIENT_CONTACT_IDS') {
            filterName = 'CLIENT_CONTACT_ID';
            values = filters.clientContactIds as string[];
          } else {
            values = Array.isArray(filters?.[filterKey])
              ? filters?.[filterKey]
              : [filters?.[filterKey]];
          }
          finalFilter.push({
            filterName,
            values: values == undefined || values?.[0] == undefined ? [] : values,
          });
          return finalFilter;
        },
        []
      ),
    [filters]
  );
  const refreshFiltersState = useRef<boolean>(false);

  const [filtersAdddedToDashboard, setFiltersAddedToDashboard] = useState<
    ComposableDashboardFiltersEnum[]
  >(() => initialiseSelectedFilters());

  const addFilterToDashboard = (filter: ComposableDashboardFiltersEnum) =>
    setFiltersAddedToDashboard((prev) => [...prev, ...[filter]]);

  const removeFilterFromDashboard = (filterEnum: ComposableDashboardFiltersEnum) =>
    setFiltersAddedToDashboard((prev) => prev.filter((prevEnum) => prevEnum != filterEnum));

  const saveAppliedFiltersPayload: () => Record<
    ComposableDashboardFiltersEnum,
    IComposableAnalyticsGlobalFilters
  > = () => {
    const enumFilterMap: Record<ComposableDashboardFiltersEnum, IComposableAnalyticsGlobalFilters> =
      {} as Record<ComposableDashboardFiltersEnum, IComposableAnalyticsGlobalFilters>;
    filtersAdddedToDashboard.forEach((filterEnum) => {
      const relevantFormFields = Object.keys(
        composableDashboardFiltersLabelValue[filterEnum].value
      );
      relevantFormFields.forEach((field) => {
        enumFilterMap[filterEnum] = { ...enumFilterMap[filterEnum], [field]: filters[field] };
        if (Object.keys(dependentFilters).includes(field))
          enumFilterMap[filterEnum] = {
            ...enumFilterMap[filterEnum],
            [field]:
              field == 'smartFilters'
                ? dependentFilters[field].map((f) => getEnumFromLabel(f))
                : dependentFilters[field],
          };
      });
    });
    return enumFilterMap;
  };

  useEffect(() => {
    const refreshStatesOfDashboardFilters = () => {
      const selectedFilters = initialiseSelectedFilters();
      const selectedFiltersPayload = initialiseSelectedFiltersPayload();
      setFiltersAddedToDashboard(selectedFilters);
      setFilters(selectedFiltersPayload);
    };
    if (refreshFiltersState.current) refreshStatesOfDashboardFilters();
  }, [initialiseSelectedFilters, initialiseSelectedFiltersPayload]);

  return (
    <ComposableDashboardFiltersContext.Provider
      value={{
        filters,
        restructuredFilters,
        setFilters,
        filtersAdddedToDashboard,
        addFilterToDashboard,
        removeFilterFromDashboard,
        saveAppliedFiltersPayload,
        refreshFiltersState,
        dependentFilters,
        setDependentFilters,
        overriddenFilters,
      }}
    >
      {props.children}
    </ComposableDashboardFiltersContext.Provider>
  );
}
